import { supabase } from "@/lib/supabase";

/** Generic list for any admin table (requires an authenticated session). */
export async function listRows<T>(table: string, orderBy = "created_at"): Promise<T[]> {
  const { data, error } = await supabase
    .from(table)
    .select("*")
    .order(orderBy, { ascending: false });
  if (error) throw error;
  return (data as T[]) || [];
}

export async function insertRow<T>(table: string, row: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.from(table).insert(row).select("*").maybeSingle();
  if (error) throw error;
  return data as T;
}

export async function updateRow(
  table: string,
  id: number,
  patch: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from(table)
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteRow(table: string, id: number): Promise<void> {
  const { error } = await supabase.from(table).delete().eq("id", id);
  if (error) throw error;
}

/** Update the same patch across many rows in one request. */
export async function updateRows(
  table: string,
  ids: number[],
  patch: Record<string, unknown>,
): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await supabase
    .from(table)
    .update({ ...patch, updated_at: new Date().toISOString() })
    .in("id", ids);
  if (error) throw error;
}

/** Find every row where a single column exactly matches a value. */
export async function findRows<T>(
  table: string,
  column: string,
  value: string,
): Promise<T[]> {
  const { data, error } = await supabase.from(table).select("*").eq(column, value);
  if (error) throw error;
  return (data as T[]) || [];
}

/** Delete many rows in one request. */
export async function deleteRows(table: string, ids: number[]): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await supabase.from(table).delete().in("id", ids);
  if (error) throw error;
}